/** Set on `<html>` while the on-screen keyboard is open (mobile). */
export const KEYBOARD_OPEN_ATTR = "data-keyboard-open";

/** Fraction of layout viewport height; below this, the keyboard is treated as open. */
const KEYBOARD_OPEN_HEIGHT_RATIO = 0.85;

const SCROLL_EDGE_PADDING_PX = 12;

function isEditableField(target: EventTarget | null): target is HTMLElement {
  return (
    target instanceof HTMLElement &&
    target.matches("input, textarea, select, [contenteditable='true']")
  );
}

function isMobileViewport(): boolean {
  return window.matchMedia("(max-width: 1023px)").matches;
}

function isKeyboardOpen(): boolean {
  if (!isMobileViewport()) return false;
  const viewport = window.visualViewport;
  const layoutHeight = window.innerHeight;
  const visibleHeight = viewport?.height ?? layoutHeight;
  return visibleHeight < layoutHeight * KEYBOARD_OPEN_HEIGHT_RATIO;
}

function syncKeyboardOpenState(): void {
  if (!isMobileViewport()) {
    document.documentElement.removeAttribute(KEYBOARD_OPEN_ATTR);
    return;
  }

  if (isKeyboardOpen()) {
    document.documentElement.setAttribute(KEYBOARD_OPEN_ATTR, "");
  } else {
    document.documentElement.removeAttribute(KEYBOARD_OPEN_ATTR);
  }
}

function getFormFlowScrollRoot(field: HTMLElement): HTMLElement | null {
  const scrollRoot = field.closest(".form-flow-scroll");
  return scrollRoot instanceof HTMLElement ? scrollRoot : null;
}

/** True when the bottom of the focused control sits under the keyboard. */
function isFieldHiddenByKeyboard(field: HTMLElement): boolean {
  if (!isKeyboardOpen()) return false;

  const scrollRoot = getFormFlowScrollRoot(field);
  if (!scrollRoot) return false;

  const fieldRect = field.getBoundingClientRect();
  const rootRect = scrollRoot.getBoundingClientRect();
  const safeBottom = rootRect.bottom - SCROLL_EDGE_PADDING_PX;

  return fieldRect.bottom > safeBottom;
}

/** Scroll down just enough to reveal a field covered by the keyboard. Never scroll up. */
function revealFieldAboveKeyboard(field: HTMLElement): void {
  if (!isFieldHiddenByKeyboard(field)) return;

  const scrollRoot = getFormFlowScrollRoot(field);
  if (!scrollRoot) return;

  const fieldRect = field.getBoundingClientRect();
  const rootRect = scrollRoot.getBoundingClientRect();
  const safeBottom = rootRect.bottom - SCROLL_EDGE_PADDING_PX;

  scrollRoot.scrollTop += fieldRect.bottom - safeBottom;
}

let scrollAfterKeyboardTimeout: ReturnType<typeof setTimeout> | null = null;

function scheduleScrollAfterKeyboard(field: HTMLElement): void {
  if (scrollAfterKeyboardTimeout) clearTimeout(scrollAfterKeyboardTimeout);
  scrollAfterKeyboardTimeout = setTimeout(() => {
    scrollAfterKeyboardTimeout = null;
    if (document.activeElement !== field && !field.contains(document.activeElement)) return;
    revealFieldAboveKeyboard(field);
  }, 300);
}

function scheduleKeyboardSync(): void {
  syncKeyboardOpenState();
  requestAnimationFrame(syncKeyboardOpenState);
  window.setTimeout(syncKeyboardOpenState, 100);
}

/** Mobile keyboard handling for full-height form flows. */
export function startVisualViewportHeightSync(): () => void {
  if (typeof window === "undefined") return () => undefined;

  let lastFocusedEditable: HTMLElement | null = null;

  function handleViewportResize(): void {
    syncKeyboardOpenState();
    if (!lastFocusedEditable || !isKeyboardOpen()) return;
    if (!isFieldHiddenByKeyboard(lastFocusedEditable)) return;
    scheduleScrollAfterKeyboard(lastFocusedEditable);
  }

  syncKeyboardOpenState();

  const viewport = window.visualViewport;
  viewport?.addEventListener("resize", handleViewportResize);
  window.addEventListener("resize", handleViewportResize);
  window.addEventListener("orientationchange", scheduleKeyboardSync);

  const onFocusIn = (event: FocusEvent): void => {
    if (!isEditableField(event.target)) return;
    lastFocusedEditable = event.target;
    scheduleKeyboardSync();
  };

  const onFocusOut = (event: FocusEvent): void => {
    if (!isEditableField(event.target)) return;
    scheduleKeyboardSync();
  };

  document.addEventListener("focusin", onFocusIn);
  document.addEventListener("focusout", onFocusOut);

  return () => {
    if (scrollAfterKeyboardTimeout) clearTimeout(scrollAfterKeyboardTimeout);
    viewport?.removeEventListener("resize", handleViewportResize);
    window.removeEventListener("resize", handleViewportResize);
    window.removeEventListener("orientationchange", scheduleKeyboardSync);
    document.removeEventListener("focusin", onFocusIn);
    document.removeEventListener("focusout", onFocusOut);
    document.documentElement.removeAttribute(KEYBOARD_OPEN_ATTR);
  };
}
